import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@marketplace/ui';
import { type PayoutStatus } from '@marketplace/contracts';
import { ContinueWithStripe } from './ContinueWithStripe.js';
import { canContinue, type OnboardingFailure } from './onboarding.js';

/**
 * `W5-T01` §4.3 — the account page's body: where payouts stand, and the way back into Stripe when the
 * next step is the provider's. Shared by `/account/payouts` and by the refresh page, which shows it
 * when it could not get a fresh link.
 */
export function PayoutsAccount({
  locale,
  payouts,
  failure,
}: {
  locale: string;
  /** `null` only on the refresh page, when the status could not be read either. */
  payouts: PayoutStatus | null;
  failure: OnboardingFailure | null;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <article data-testid="payouts-account">
      <h1>{t('payouts.account.link')}</h1>
      {payouts === null ? null : (
        <p className="mp-lead" data-testid="payouts-status">
          {t(`payouts.status.${payouts.status}`)}
        </p>
      )}
      {failure === null ? null : <Alert tone="error">{t(failure)}</Alert>}
      {payouts !== null && canContinue(payouts.status) ? (
        <ContinueWithStripe locale={locale} from="account" label={t('payouts.action.continue')} />
      ) : null}
    </article>
  );
}
